import { camelCase, keyBy } from 'lodash';
import { useMemo } from 'react';
import { FieldSectionType } from 'api/field-sections/types';
import {
  InferredProgressOptions,
  ProjectFinancialsContractType,
  ProjectFinancialsSubscriptionFrequency,
} from 'api/project';
import { ResourceManagementPermissionsResponse } from 'api/resource-management/types';
import {
  CustomRevenueRecognitionConfigApi,
  CustomRevenueRecognitionTypes,
  FixedFeeRevenueRecognitionTypes,
} from 'api/settings-financials/types';
import { FieldOptionBase } from 'api/tasks';
import {
  FieldType,
  FilterableField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import {
  Translate,
  TranslationKey,
  useTranslate,
  useTranslation,
} from 'hooks/use-translation';
import { useProjectFieldsQuery } from 'queries';
import { useCustomRevenueRecognitionQuery } from 'queries/financial-notifications';
import { getRandomColor } from 'utils/get-random-color';
import { useCurrencyField } from './use-currency-field';
import { useCurrentPhasesField } from './use-current-phases-field';
import { useCustomerField } from './use-customer-field';
import { usePartnerCompanyField } from './use-partner-company-field';
import { useProjectOwnerField } from './use-project-owner-field';
import { useTeamMembersField } from './use-team-members-field';
import { useTemplateField } from './use-template-field';

const FIELD_ID_ARR = -1;
const FIELD_ID_PROJECT_FEE = FIELD_ID_ARR - 1;
const FIELD_ID_PROJECT_OWNER = FIELD_ID_PROJECT_FEE - 1;
const FIELD_ID_TEMPLATE = FIELD_ID_PROJECT_OWNER - 1;
const FIELD_ID_INFERRED_PROGRESS = FIELD_ID_TEMPLATE - 1;
const FIELD_ID_DUE_DATE = FIELD_ID_INFERRED_PROGRESS - 1;
const FIELD_ID_START_DATE = FIELD_ID_DUE_DATE - 1;
const FIELD_ID_LAST_STATUS_UPDATE = FIELD_ID_START_DATE - 1;
const FIELD_ID_ESTIMATED_DUE_DATE = FIELD_ID_LAST_STATUS_UPDATE - 1;

export const FIELD_ID_PROJECT_NAME = FIELD_ID_ESTIMATED_DUE_DATE - 1;
const FIELD_ID_CUSTOMER = FIELD_ID_PROJECT_NAME - 1;
const FIELD_ID_PROGRESS_PERCENT = FIELD_ID_CUSTOMER - 1;
const FIELD_ID_TEAM_MEMBERS = FIELD_ID_PROGRESS_PERCENT - 1;
const FIELD_ID_CURRENT_PHASES = FIELD_ID_TEAM_MEMBERS - 1;
const FIELD_ID_CREATED_ON = FIELD_ID_CURRENT_PHASES - 1;

export const FIELD_ID_RATINGS = FIELD_ID_CURRENT_PHASES - 1;
const FIELD_ID_PARNTER_COMPANIES = FIELD_ID_RATINGS - 1;
const FIELD_ID_PROJECT_AGE = FIELD_ID_PARNTER_COMPANIES - 1;
const FIELD_ID_PLANNED_DURATION = FIELD_ID_PROJECT_AGE - 1;
const FIELD_ID_ACTUAL_START_DATE = FIELD_ID_PLANNED_DURATION - 1;
const FIELD_ID_ACTUAL_COMPLETED_DATE = FIELD_ID_ACTUAL_START_DATE - 1;
const FIELD_ID_REMAINING_MINUTES = FIELD_ID_ACTUAL_COMPLETED_DATE - 1;
const FIELD_ID_BUDGETED_HOURS = FIELD_ID_REMAINING_MINUTES - 1;
const FIELD_ID_TRACKED_HOURS = FIELD_ID_BUDGETED_HOURS - 1;
const FIELD_ID_ALLOCATED_HOURS = FIELD_ID_TRACKED_HOURS - 1;
const FIELD_ID_CUSTOMERS_INVITED = FIELD_ID_ALLOCATED_HOURS - 1;
const FIELD_ID_CUSTOMERS_ACCEPTED = FIELD_ID_CUSTOMERS_INVITED - 1;
const FIELD_ID_PROJECT_BUDGET = FIELD_ID_CUSTOMERS_ACCEPTED - 1;
const FIELD_ID_FINANCIAL_CONTRACT_TYPE = FIELD_ID_PROJECT_BUDGET - 1;
const FIELD_ID_ESTIMATE_AT_COMPLETION = FIELD_ID_FINANCIAL_CONTRACT_TYPE - 1;
const FIELD_ID_ESTIMATE_TO_COMPLETION = FIELD_ID_ESTIMATE_AT_COMPLETION - 1;
const FIELD_ID_CURRENCY = FIELD_ID_ESTIMATE_TO_COMPLETION - 1;
const FIELD_ID_ACTUAL_REVENUE = FIELD_ID_CURRENCY - 1;
const FIELD_ID_ACTUAL_COST = FIELD_ID_ACTUAL_REVENUE - 1;
const FIELD_ID_ACTUAL_PROFIT = FIELD_ID_ACTUAL_COST - 1;
const FIELD_ID_ACTUAL_MARGIN = FIELD_ID_ACTUAL_PROFIT - 1;
const FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED = FIELD_ID_ACTUAL_MARGIN - 1;
const FIELD_ID_PERCENTAGE_BUDGET_CONSUMED =
  FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED - 1;
const FIELD_ID_SUBSCRIPTION_FREQUENCY = FIELD_ID_PERCENTAGE_BUDGET_CONSUMED - 1;
const FIELD_ID_NUMBER_OF_PERIODS = FIELD_ID_SUBSCRIPTION_FREQUENCY - 1;
const FIELD_ID_REVENUE_RECOGNITION_TYPE = FIELD_ID_NUMBER_OF_PERIODS - 1;
const FIELD_ID_CURRENT_PERIOD = FIELD_ID_REVENUE_RECOGNITION_TYPE - 1;
const FIELD_ID_CURRENT_PERIOD_BUDGET = FIELD_ID_CURRENT_PERIOD - 1;
const FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS = FIELD_ID_CURRENT_PERIOD_BUDGET - 1;
const FIELD_ID_CURRENT_PERIOD_START_DATE =
  FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS - 1;
const FIELD_ID_CURRENT_PERIOD_END_DATE = FIELD_ID_CURRENT_PERIOD_START_DATE - 1;
const FIELD_ID_CURRENT_PERIOD_REVENUE = FIELD_ID_CURRENT_PERIOD_END_DATE - 1;
const FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS =
  FIELD_ID_CURRENT_PERIOD_REVENUE - 1;

export const ProjectFinancialsContractTypeOptions: FieldOptionBase<ProjectFinancialsContractType>[] =
  [
    {
      label: 'Fixed fee',
      value: ProjectFinancialsContractType.FIXED,
      color: 'green',
      active: true,
    },
    {
      label: 'Time & material',
      value: ProjectFinancialsContractType.TM,
      color: 'blue',
      active: true,
    },
    {
      label: 'Non billable',
      value: ProjectFinancialsContractType.NONBILLABLE,
      color: 'gray',
      active: true,
    },
    {
      label: 'Subscription',
      value: ProjectFinancialsContractType.SUBSCRIPTION,
      color: 'purple',
      active: true,
    },
  ];

const getRevenueRecognitionOptions = (
  translate: Translate<string, string>,
  customRevenueRecognitionConfigs: CustomRevenueRecognitionConfigApi[] = [],
) =>
  Object.values(FixedFeeRevenueRecognitionTypes)
    .map((value) => ({
      label: translate(
        `settings.financials.revenue_recognition.${value}` as TranslationKey,
      ),
      value: value.toString(),
      active: true,
      color: getRandomColor(value.toString()),
    }))
    .concat(
      customRevenueRecognitionConfigs.map((config) => ({
        label: config.name,
        // Setting value this way because of filters. Filters can't handle string value & number value for the same field, even if you stringify the number.
        // You'll find the same in transformer for project list data.
        value: `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${config.customRevenueRecognitionConfigId}`,
        active: true,
        color: getRandomColor(config.customRevenueRecognitionConfigId),
      })),
    );

export const ProjectFinancialsSubscriptionFrequencyOptions: FieldOptionBase<ProjectFinancialsSubscriptionFrequency>[] =
  [
    {
      label: 'Monthly',
      value: ProjectFinancialsSubscriptionFrequency.Monthly,
      color: 'green',
      active: true,
    },
    {
      label: 'Quarterly',
      value: ProjectFinancialsSubscriptionFrequency.Quarterly,
      color: 'blue',
      active: true,
    },
    {
      label: 'Yearly',
      value: ProjectFinancialsSubscriptionFrequency.Yearly,
      color: 'purple',
      active: true,
    },
  ];

export const ProjectTimeTrackingFieldsName = {
  trackedHours: 'totalMinutes',
  allocatedHours: 'totalAllocatedHours',
  budgetedMinutes: 'budgetedMinutes',
  remainingMinutes: 'remainingMinutes',
  billableMinutes: 'billableMinutes',
};

export const useProjectNativeFieldsAsFilterableFields = (
  {
    peopleIHaveAccessTo,
  }: { peopleIHaveAccessTo?: ResourceManagementPermissionsResponse } = {
    peopleIHaveAccessTo: undefined,
  },
): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const translate = useTranslate();
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const arr_label = useTranslation('project.fields.arr');
  const project_name_label = useTranslation('project.fields.project_name');
  const project_fee_label = useTranslation('project.fields.project_fee');
  const due_date_label = useTranslation('due_date');
  const start_date_label = useTranslation('start_date');
  const last_status_update_label = useTranslation(
    'project.fields.last_status_update',
  );
  const [isTimeSheetEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);

  const progress_percent_label = useTranslation(
    'project.fields.progress_percent',
  );
  const inferred_progress_label = useTranslation(
    'project.fields.inferred_progress',
  );
  const project_age_label = useTranslation('project.fields.project_age');
  const planned_duration_label = useTranslation(
    'project.fields.planned_duration',
  );
  const actual_start_date_label = useTranslation(
    'project.fields.actual_start_date',
  );
  const actual_completed_date_label = useTranslation(
    'project.fields.actual_completed_date',
  );
  const trackedHoursLabel = useTranslation('project.fields.tracked_hours');
  const budgetHoursLabel = useTranslation('project.fields.budgeted_hours');
  const remainingMinutesLabel = useTranslation(
    'project.fields.remaining_minutes',
  );
  const allocatedHoursLabel = useTranslation('project.fields.allocated_hours');
  const customersInvitedLabel = useTranslation(
    'project.fields.customers_invited',
  );
  const customersAcceptedLabel = useTranslation(
    'project.fields.customers_accepted',
  );
  const projectCreatedOnLabel = useTranslation('project.fields.created_on');
  const projectBudgetLabel = useTranslation('project.fields.project_budget');
  const actualRevenueLabel = useTranslation('project.fields.actual_revenue');
  const actualCostLabel = useTranslation('project.fields.actual_cost');
  const actualProfitLabel = useTranslation('project.fields.actual_profit');
  const actualProfitMarginLabel = useTranslation(
    'project.fields.actual_profit_margin',
  );
  const estimateAtCompletionLabel = useTranslation(
    'project.fields.estimate_at_completion',
  );
  const estimateToCompletionLabel = useTranslation(
    'project.fields.estimate_to_completion',
  );
  const financialContractTypeLabel = useTranslation(
    'project.fields.financial_contract_type',
  );
  const revenueRecognitionTypeLabel = useTranslation(
    'project.fields.revenue_recognition_type',
  );
  const percentageBudgetedHoursConsumedLabel = useTranslation(
    'project.fields.percentage_budgeted_hours_consumed',
  );
  const percentageBudgetConsumedLabel = useTranslation(
    'project.fields.percentage_budget_consumed',
  );
  const subscriptionFrequencyLabel = useTranslation(
    'project.fields.subscription_frequency',
  );
  const numberOfPeriodsLabel = useTranslation(
    'project.fields.number_of_periods',
  );
  const currentPeriodLabel = useTranslation('project.fields.current_period');
  const currentPeriodBudgetLabel = useTranslation(
    'project.fields.current_period_budget',
  );
  const currentPeriodBudgetedMinutesLabel = useTranslation(
    'project.fields.current_period_budgeted_hours',
  );
  const currentPeriodRevenueLabel = useTranslation(
    'project.fields.current_period_revenue',
  );
  const currentPeriodTrackedHoursLabel = useTranslation(
    'project.fields.current_period_tracked_hours',
  );
  const currentPeriodStartDateLabel = useTranslation(
    'project.fields.current_period_start_date',
  );
  const currentPeriodEndDateLabel = useTranslation(
    'project.fields.current_period_end_date',
  );

  const { data: projectFieldsData, isLoading: isProjectFieldsLoading } =
    useProjectFieldsQuery();

  const { hasAccessToResource } = useWithAccess();

  const canAccessProfitability = hasAccessToResource({
    rule: 'PROJECT_PROFITABILITY:READ',
  });

  const canViewCostRates = useMemo(
    () => isFinancialsEnabled && canAccessProfitability,
    [canAccessProfitability, isFinancialsEnabled],
  );
  const { data: customRevenueRecognitionConfigs } =
    useCustomRevenueRecognitionQuery(false, {
      enabled: isFinancialsEnabled && !isCurrentUserPartner,
    });

  const projectFieldsMapByName = useMemo(
    () => keyBy(projectFieldsData ?? [], 'fieldName'),
    [projectFieldsData],
  );

  const arrProjectFee = useMemo(() => {
    if (isProjectFieldsLoading) {
      return [];
    }
    const fields = projectFieldsData?.map((field) => {
      if (field.fieldName === 'annualizedRecurringRevenue' && field.isEnabled) {
        return {
          fieldName: 'arr',
          fieldLabel: arr_label,
          type: FieldType.Number,
          fieldId: FIELD_ID_ARR,
          fieldIndex: field.fieldIndex,
        };
      }
      if (field.fieldName === 'projectFee' && field.isEnabled) {
        return {
          fieldName: 'projectFee',
          fieldLabel: project_fee_label,
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_FEE,
          fieldIndex: field.fieldIndex,
        };
      }

      return null;
    });

    if (fields) {
      return fields.filter(Boolean);
    }

    return [];
  }, [arr_label, project_fee_label, projectFieldsData, isProjectFieldsLoading]);

  const projectFields = useMemo(
    () =>
      [
        {
          fieldName: 'projectName',
          fieldLabel: project_name_label,
          type: FieldType.Text,
          fieldId: FIELD_ID_PROJECT_NAME,
        },
        {
          fieldName: 'inferredProgress',
          fieldLabel: inferred_progress_label,
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_INFERRED_PROGRESS,

          optionsAvailable: true,
          metaData: {
            options: InferredProgressOptions,
          },
        },
        {
          fieldName: 'dueDate',
          fieldLabel: due_date_label,
          type: FieldType.Date,
          fieldId: FIELD_ID_DUE_DATE,
        },
        {
          fieldName: 'startDate',
          fieldLabel: start_date_label,
          type: FieldType.Date,
          fieldId: FIELD_ID_START_DATE,
        },
        {
          fieldLabel: last_status_update_label,
          fieldName: 'lastStatusUpdate',
          type: FieldType.Date,
          fieldId: FIELD_ID_LAST_STATUS_UPDATE,
        },
        {
          fieldLabel: progress_percent_label,
          fieldName: 'progressPercent',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROGRESS_PERCENT,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: project_age_label,
          fieldName: 'projectAge',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_AGE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: planned_duration_label,
          fieldName: 'plannedDuration',
          type: FieldType.Number,
          fieldId: FIELD_ID_PLANNED_DURATION,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: actual_start_date_label,
          fieldName: 'startDateActual',
          type: FieldType.Date,
          fieldId: FIELD_ID_ACTUAL_START_DATE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: actual_completed_date_label,
          fieldName: 'dueDateActual',
          type: FieldType.Date,
          fieldId: FIELD_ID_ACTUAL_COMPLETED_DATE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: customersInvitedLabel,
          fieldName: 'customerMembersInvited',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_INVITED,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: customersAcceptedLabel,
          fieldName: 'customerMembersAccepted',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_ACCEPTED,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: projectCreatedOnLabel,
          fieldName: 'createdAt',
          type: FieldType.Date,
          fieldId: FIELD_ID_CREATED_ON,
        },
        ...arrProjectFee,
        ...(isFinancialsEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: projectBudgetLabel,
                fieldName: 'projectBudget',
                type: FieldType.Number,
                fieldId: FIELD_ID_PROJECT_BUDGET,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: financialContractTypeLabel,
                fieldName: 'financialContractType',
                type: FieldType.SingleSelect,
                fieldId: FIELD_ID_FINANCIAL_CONTRACT_TYPE,
                optionsAvailable: true,
                metaData: {
                  options: ProjectFinancialsContractTypeOptions,
                },
              },
              {
                fieldLabel: revenueRecognitionTypeLabel,
                fieldName: 'revenueRecognitionType',
                type: FieldType.SingleSelect,
                fieldId: FIELD_ID_REVENUE_RECOGNITION_TYPE,
                optionsAvailable: true,
                metaData: {
                  options: getRevenueRecognitionOptions(
                    translate,
                    customRevenueRecognitionConfigs,
                  ),
                },
              },
              {
                fieldLabel: actualRevenueLabel,
                fieldName: 'actualRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_ACTUAL_REVENUE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: percentageBudgetConsumedLabel,
                fieldName: 'percentageBudgetConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGET_CONSUMED,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: subscriptionFrequencyLabel,
                fieldName: 'subscriptionFrequency',
                type: FieldType.SingleSelect,
                fieldId: FIELD_ID_SUBSCRIPTION_FREQUENCY,
                optionsAvailable: true,
                metaData: {
                  options: ProjectFinancialsSubscriptionFrequencyOptions,
                },
              },
              {
                fieldLabel: numberOfPeriodsLabel,
                fieldName: 'numberOfPeriods',
                type: FieldType.Number,
                fieldId: FIELD_ID_NUMBER_OF_PERIODS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodLabel,
                fieldName: 'currentPeriodNumber',
                type: FieldType.Number,
                fieldId: FIELD_ID_CURRENT_PERIOD,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodStartDateLabel,
                fieldName: 'currentPeriodStartDate',
                type: FieldType.Date,
                fieldId: FIELD_ID_CURRENT_PERIOD_START_DATE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodEndDateLabel,
                fieldName: 'currentPeriodEndDate',
                type: FieldType.Date,
                fieldId: FIELD_ID_CURRENT_PERIOD_END_DATE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodBudgetLabel,
                fieldName: 'periodBudget',
                type: FieldType.Number,
                fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodBudgetedMinutesLabel,
                fieldName: 'periodMinutes',
                type: FieldType.Number,
                fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodRevenueLabel,
                fieldName: 'periodRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_CURRENT_PERIOD_REVENUE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: currentPeriodTrackedHoursLabel,
                fieldName: 'periodTotalTimeTracked',
                type: FieldType.Number,
                fieldId: FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS,
                metaData: {
                  required: true,
                },
              },
              ...(canViewCostRates
                ? [
                    {
                      fieldLabel: actualCostLabel,
                      fieldName: 'actualCost',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_COST,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: actualProfitLabel,
                      fieldName: 'actualProfit',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_PROFIT,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: actualProfitMarginLabel,
                      fieldName: 'actualProfitMargin',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_MARGIN,
                      metaData: {
                        required: true,
                      },
                    },
                  ]
                : []),
            ]
          : []),
        ...(isTimeSheetEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: trackedHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.trackedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_TRACKED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: budgetHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.budgetedMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_BUDGETED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: remainingMinutesLabel,
                fieldName: ProjectTimeTrackingFieldsName.remainingMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_REMAINING_MINUTES,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: allocatedHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.allocatedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_ALLOCATED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: estimateAtCompletionLabel,
                fieldName: 'estimateAtCompletion',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_AT_COMPLETION,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: estimateToCompletionLabel,
                fieldName: 'estimateToComplete',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_TO_COMPLETION,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: percentageBudgetedHoursConsumedLabel,
                fieldName: 'percentageBudgetedHoursConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED,
                metaData: {
                  required: true,
                },
              },
            ]
          : []),
      ].map(
        (x) =>
          ({
            ...x,
            customField: false,
            private: false,
            sourceType: SourceType.Project,
            fieldIndex:
              projectFieldsMapByName[camelCase(x?.fieldName)]?.fieldIndex,
            objectType: FieldSectionType.Project,
          } as FilterableField),
      ),
    [
      project_name_label,
      inferred_progress_label,
      due_date_label,
      start_date_label,
      last_status_update_label,
      progress_percent_label,
      project_age_label,
      planned_duration_label,
      actual_start_date_label,
      actual_completed_date_label,
      customersInvitedLabel,
      customersAcceptedLabel,
      projectCreatedOnLabel,
      arrProjectFee,
      isFinancialsEnabled,
      isCurrentUserPartner,
      projectBudgetLabel,
      financialContractTypeLabel,
      revenueRecognitionTypeLabel,
      translate,
      customRevenueRecognitionConfigs,
      actualRevenueLabel,
      percentageBudgetConsumedLabel,
      subscriptionFrequencyLabel,
      numberOfPeriodsLabel,
      currentPeriodLabel,
      currentPeriodStartDateLabel,
      currentPeriodEndDateLabel,
      currentPeriodBudgetLabel,
      currentPeriodBudgetedMinutesLabel,
      currentPeriodRevenueLabel,
      currentPeriodTrackedHoursLabel,
      canViewCostRates,
      actualCostLabel,
      actualProfitLabel,
      actualProfitMarginLabel,
      isTimeSheetEnabled,
      trackedHoursLabel,
      remainingMinutesLabel,
      budgetHoursLabel,
      allocatedHoursLabel,
      estimateAtCompletionLabel,
      estimateToCompletionLabel,
      percentageBudgetedHoursConsumedLabel,
      projectFieldsMapByName,
    ],
  );
  const { templateField, isLoading: isTemplateFieldLoading } =
    useTemplateField(FIELD_ID_TEMPLATE);
  const { projectOwnerField, isLoading: isProjectOwnerFieldLoading } =
    useProjectOwnerField(FIELD_ID_PROJECT_OWNER);
  const { customerField, isLoading: isCustomerFieldLoading } =
    useCustomerField(FIELD_ID_CUSTOMER);
  const { teamMemberField, isLoading: isTeamMembersFieldLoading } =
    useTeamMembersField(FIELD_ID_TEAM_MEMBERS, peopleIHaveAccessTo);
  const { currentPhasesField, isLoading: isCurrentPhasesFieldLoading } =
    useCurrentPhasesField(FIELD_ID_CURRENT_PHASES);
  const {
    partnerCompaniesField,
    isLoading: isPartnerCompaniesFieldLoading,
    isAnyPartnerCompanyAvailable,
  } = usePartnerCompanyField(FIELD_ID_PARNTER_COMPANIES);
  const { currencyField, isLoading: isCurrencyFieldLoading } =
    useCurrencyField(FIELD_ID_CURRENCY);

  const fields = useMemo(
    () =>
      [
        ...projectFields,
        templateField,
        projectOwnerField,
        customerField,
        teamMemberField,
        currentPhasesField,
        isAnyPartnerCompanyAvailable ? partnerCompaniesField : undefined,
        currencyField,
      ].filter((x) => Boolean(x)) as FilterableField[],
    [
      projectFields,
      templateField,
      projectOwnerField,
      customerField,
      teamMemberField,
      currentPhasesField,
      isAnyPartnerCompanyAvailable,
      partnerCompaniesField,
      currencyField,
    ],
  );

  const isFieldsLoading = useMemo(
    () =>
      isProjectOwnerFieldLoading ||
      isCustomerFieldLoading ||
      isTeamMembersFieldLoading ||
      isCurrentPhasesFieldLoading ||
      isTemplateFieldLoading ||
      isPartnerCompaniesFieldLoading ||
      isCurrencyFieldLoading,
    [
      isProjectOwnerFieldLoading,
      isCustomerFieldLoading,
      isTeamMembersFieldLoading,
      isCurrentPhasesFieldLoading,
      isTemplateFieldLoading,
      isPartnerCompaniesFieldLoading,
      isCurrencyFieldLoading,
    ],
  );

  return useMemo(
    () => ({
      fields,
      loading: isFieldsLoading,
    }),
    [fields, isFieldsLoading],
  );
};

export const useProjectNativeFieldsForVariables = () => {
  const { fields, loading } = useProjectNativeFieldsAsFilterableFields();

  return useMemo(
    () => ({
      loading,
      fields: fields.filter(({ fieldId }) =>
        [
          FIELD_ID_PROJECT_NAME,
          FIELD_ID_START_DATE,
          FIELD_ID_DUE_DATE,
          FIELD_ID_ARR,
          FIELD_ID_PROJECT_FEE,
          FIELD_ID_BUDGETED_HOURS,
        ].includes(fieldId),
      ),
    }),
    [fields, loading],
  );
};
